import { ESPESSURA_MINIMA_INICIAL, TRATIO_INICIAL_QT } from '../../cptu/motor';
import type { ParametrosDoTRatio } from '../../cptu/motor';
import { chaveDasEscalas } from '../../cptu/visual/escalaDoGrafico';

/**
 * Os parâmetros do T-ratio da janela «Camadas automáticas» (onda 347, spec 2026-10-22 §6), guardados no aparelho: a janela os
 * grava (Tarefa 10) e a sugestão do 3D os lê (Tarefa 9, §4.6). O valor é o JSON de `ParametrosDoTRatio` (`janela`, `limite`,
 * `grandeza`, `espessuraMinima`, `juntarPorZona`, `espacamentoMinimo`), solto ou dentro de `{ tratio: … }`. POR USUÁRIO (os
 * celulares do laboratório são compartilhados): `labmanager.cptu.camadas.parametros.<id>`, a regra de chave das escalas
 * (`chaveDasEscalas`); a janela grava e o 3D lê na chave de quem está na sessão (`chaveDosParametrosDasCamadas`).
 */
export const CHAVE_DOS_PARAMETROS_DAS_CAMADAS = 'labmanager.cptu.camadas.parametros';

/** A chave dos parâmetros de quem está na sessão: `labmanager.cptu.camadas.parametros.<id>`; sem quem, a base. */
export function chaveDosParametrosDasCamadas(quem: string | null | undefined): string {
  return chaveDasEscalas(CHAVE_DOS_PARAMETROS_DAS_CAMADAS, quem);
}

/** Os de partida (os da prévia): o T-ratio sobre log₁₀ qt, janela 2,00 m, limite 6, espessura mínima 0,50 m, sem juntar por zona. */
export const PARAMETROS_DO_TRATIO_DE_PARTIDA: Readonly<ParametrosDoTRatio> = Object.freeze({
  ...TRATIO_INICIAL_QT, grandeza: 'log_qt', espessuraMinima: ESPESSURA_MINIMA_INICIAL, juntarPorZona: false,
});

/**
 * As faixas do que se aceita do aparelho (m, ou adimensional no limite de T): só a sanidade do valor guardado, para um número
 * corrompido não virar sugestão absurda — não é regra técnica (os valores de partida é que estão a assinar pelo RT).
 * ESCOLHA DA TELA, SEM FONTE [3d.camadas-faixas-dos-parametros] (na lista `ESCOLHAS_DO_3D`).
 */
// ESCOLHA DA TELA, SEM FONTE [3d.camadas-faixas-dos-parametros]
export const FAIXAS_DOS_PARAMETROS_DO_TRATIO = Object.freeze({
  janela: { min: 0.05, max: 20 }, limite: { min: 0, max: 1000 }, espessuraMinima: { min: 0, max: 20 }, espacamentoMinimo: { min: 0.01, max: 20 },
});

/**
 * Os parâmetros do T-ratio que valem (pura): os campos que servem do guardado; o que falta, não é do tipo ou sai da faixa
 * (`FAIXAS_DOS_PARAMETROS_DO_TRATIO`), o de partida, campo a campo.
 */
export function parametrosDoTRatioValidos(x: unknown): ParametrosDoTRatio {
  const p = PARAMETROS_DO_TRATIO_DE_PARTIDA;
  const f = FAIXAS_DOS_PARAMETROS_DO_TRATIO;
  const bruto = (x && typeof x === 'object' ? x : {}) as Record<string, unknown>;
  const o = (bruto.tratio && typeof bruto.tratio === 'object' ? bruto.tratio : bruto) as Record<string, unknown>;
  const naFaixa = <T extends number | undefined>(v: unknown, faixa: { min: number; max: number }, padrao: T): number | T =>
    (typeof v === 'number' && Number.isFinite(v) && v >= faixa.min && v <= faixa.max ? v : padrao);
  const saida: ParametrosDoTRatio = {
    janela: naFaixa(o.janela, f.janela, p.janela),
    limite: naFaixa(o.limite, f.limite, p.limite),
    grandeza: o.grandeza === 'ic' || o.grandeza === 'log_qt' ? o.grandeza : p.grandeza,
    espessuraMinima: naFaixa(o.espessuraMinima, f.espessuraMinima, p.espessuraMinima),
    juntarPorZona: typeof o.juntarPorZona === 'boolean' ? o.juntarPorZona : p.juntarPorZona,
  };
  const espacamento = naFaixa(o.espacamentoMinimo, f.espacamentoMinimo, undefined);
  if (espacamento !== undefined) saida.espacamentoMinimo = espacamento;
  return saida;
}

/** Os parâmetros guardados no aparelho por quem está na sessão; sem eles (ou sem aparelho), os de partida. Nunca lança. */
export function parametrosDoTRatioGuardados(quem: string | null | undefined): ParametrosDoTRatio {
  try {
    const bruto = localStorage.getItem(chaveDosParametrosDasCamadas(quem));
    return parametrosDoTRatioValidos(bruto ? JSON.parse(bruto) : null);
  } catch {
    return parametrosDoTRatioValidos(null);
  }
}

/**
 * O aviso, na mesma página, de que a janela «Camadas automáticas» gravou parâmetros novos (Tarefa 10; decisão R33): a sugestão
 * do 3D, que lê os guardados uma vez, recalcula sem recarregar. Outra aba avisa pelo `storage` do navegador.
 */
export const EVENTO_DOS_PARAMETROS_DAS_CAMADAS = 'labmanager:cptu-camadas-parametros';

/**
 * Guarda os parâmetros da janela no aparelho (o JSON de `ParametrosDoTRatio`, solto) e avisa a página. O que a janela não
 * edita e já estava guardado (o `espacamentoMinimo`) fica: os novos entram por cima dos guardados. Nunca lança.
 */
export function gravarParametrosDoTRatio(quem: string | null | undefined, p: ParametrosDoTRatio): void {
  try {
    localStorage.setItem(chaveDosParametrosDasCamadas(quem),
      JSON.stringify(parametrosDoTRatioValidos({ ...parametrosDoTRatioGuardados(quem), ...p })));
  } catch { /* sem aparelho: vale nesta tela só */ }
  try {
    window.dispatchEvent(new Event(EVENTO_DOS_PARAMETROS_DAS_CAMADAS));
  } catch { /* sem janela */ }
}

/**
 * Chama `aoMudar` quando os parâmetros guardados de quem está na sessão mudam (nesta página ou em outra aba); devolve o
 * desligar. Nesta página o aviso não diz de quem: reler os de quem está na sessão é inofensivo.
 */
export function ouvirParametrosDoTRatio(quem: string | null | undefined, aoMudar: () => void): () => void {
  const chave = chaveDosParametrosDasCamadas(quem);
  const daOutraAba = (e: StorageEvent): void => { if (e.key === null || e.key === chave) aoMudar(); };
  window.addEventListener(EVENTO_DOS_PARAMETROS_DAS_CAMADAS, aoMudar);
  window.addEventListener('storage', daOutraAba);
  return () => {
    window.removeEventListener(EVENTO_DOS_PARAMETROS_DAS_CAMADAS, aoMudar);
    window.removeEventListener('storage', daOutraAba);
  };
}
